import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout/Layout';
import HomePage from './components/Home/HomePage';
import RegisterForm from './components/Auth/RegisterForm';
import LoginForm from './components/Auth/LoginForm';
import ForgotPasswordForm from './components/Auth/ForgotPasswordForm';
import ResetPasswordForm from './components/Auth/ResetPasswordForm';
import Dashboard from './components/Dashboard/Dashboard';
import TotalBasesPredictions from './components/Predictions/TotalBasesPredictions';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import axios from 'axios';
import RegisterInterestForm from './components/Auth/RegisterInterestForm';
import HitsPredictions from './components/Predictions/HitsPredictions';
import TwoHitsPredictions from './components/Predictions/TwoHitsPredictions';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import HRPredictions from './components/Predictions/HRPredictions';
import ModelStatsPage from './pages/ModelStatsPage';
import ProfilePage from './pages/ProfilePage';
import ScrollToTop from './components/Layout/ScrollToTop';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminModelDebugPage from './pages/AdminModelDebugPage';
import AdminRunJobPage from './pages/AdminRunJobPage';
import AdminSocialCardsPage from './pages/AdminSocialCardsPage';
import SocialCardExportPage from './pages/SocialCardExportPage';
import AdminEmailPreviewPage from './pages/AdminEmailPreviewPage';
import WhyUsPage from './pages/WhyUsPage';
import TipJarPage from './pages/TipJarPage';
import GamePicksPage from './pages/GamePicksPage';
import TeamPicksPage from './pages/TeamPicksPage';
import TrackedPicksPage from './pages/TrackedPicksPage';
import ContactPage from './pages/ContactPage';
import AboutUsPage from './pages/AboutUsPage';
import ResourcesLandingPage from './pages/ResourcesLandingPage';
import OddsCalculatorPage from './pages/OddsCalculatorPage';
import ParlayCalculatorPage from './pages/ParlayCalculatorPage';
import BettingStrategiesPage from './pages/BettingStrategiesPage';
import HowToUsePage from './pages/HowToUsePage';
import HowItWorksPage from './pages/HowItWorksPage';

// Set axios base URL
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,     // 2 minutes — data considered fresh
      gcTime: 10 * 60 * 1000,       // 10 minutes — keep unused cache entries
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <ScrollToTop />
          <Layout>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/register" element={<RegisterForm />} />
              <Route path="/early-access" element={<RegisterInterestForm />} />
              <Route path="/login" element={<LoginForm />} />
              <Route path="/forgot-password" element={<ForgotPasswordForm />} />
              <Route path="/reset-password" element={<ResetPasswordForm />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/mlb/total-bases" element={<TotalBasesPredictions />} />
              <Route path="/mlb/hit" element={<HitsPredictions />} />
              <Route path="/mlb/hits" element={<Navigate to="/mlb/hit" replace />} />
              <Route path="/mlb/two-hits" element={<TwoHitsPredictions />} />
              <Route path="/mlb/hr" element={<HRPredictions />} />
              <Route path="/model-stats" element={<ModelStatsPage />} />
              <Route path="/game-picks" element={<GamePicksPage />} />
              <Route path="/team-picks" element={<TeamPicksPage />} />
              <Route path="/why-us" element={<WhyUsPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/about" element={<AboutUsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/resources" element={<ResourcesLandingPage />} />
              <Route path="/resources/odds-calculator" element={<OddsCalculatorPage />} />
              <Route path="/resources/parlay-calculator" element={<ParlayCalculatorPage />} />
              <Route path="/resources/betting-strategies" element={<BettingStrategiesPage />} />
              <Route path="/resources/how-to-use" element={<HowToUsePage />} />
              <Route path="/tip-jar" element={<TipJarPage />} />
              {/* Headless social card capture (signed token); not linked in nav */}
              <Route path="/social-export" element={<SocialCardExportPage />} />
              
              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/tracked-picks" element={<TrackedPicksPage />} />
              </Route>
              <Route element={<ProtectedRoute adminOnly={true} />}>
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/run-job" element={<AdminRunJobPage />} />
                <Route path="/admin/social-media" element={<AdminSocialCardsPage />} />
                <Route path="/admin/email-preview" element={<AdminEmailPreviewPage />} />
                {process.env.REACT_APP_DEBUG_TOOLS === 'true' && (
                  <Route path="/admin/debug-model" element={<AdminModelDebugPage />} />
                )}
              </Route>
              
              {/* 404 route */}
              <Route path="*" element={
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '70vh',
                  flexDirection: 'column',
                  textAlign: 'center',
                  padding: '0 1rem'
                }}>
                  <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>404 - Page Not Found</h1>
                  <p style={{ fontSize: '1.125rem', marginBottom: '2rem' }}>
                    The page you're looking for doesn't exist or has been moved.
                  </p>
                  <a 
                    href="/" 
                    style={{ 
                      padding: '0.75rem 1.5rem', 
                      backgroundColor: '#4299e1', 
                      color: 'white', 
                      borderRadius: '0.25rem', 
                      textDecoration: 'none',
                      fontWeight: '500'
                    }}
                  >
                    Go Home
                  </a>
                </div>
              } />
            </Routes>
          </Layout>
        </Router>
      </ThemeProvider>
    </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
